var m = require('mithril');
var prop = require('mithril/stream');

var dragscroll = require('../../libs/dragscroll');
var loaders = require('../../layout/components/loaders');
var header = require('../../layout/components/header');
var heading = require('../../components/heading');
var io = require('../../libs/socketio');
var alarmsTablePagination = require('../../alarms/alarmsTablePagination');
var agentGatewayStatus = require('../agentGatewayStatus');
var agentHistoryChart = require('./agentHistoryChart');

var page = {};

var lastRowRects = new Map(); // rowKey -> DOMRect-like

page.oninit = function () {
  var ctrl = this;

  header.vm.activeTab('Agents');
  document.title = 'nimrod | Agents';

  ctrl.state = prop({ updatedAt: null, workers: [] });
  ctrl.error = prop(null);
  ctrl.history = prop(null);
  ctrl.historyError = prop(null);
  ctrl.historyLoading = prop(false);
  ctrl.sort = prop({ key: 'gateway', dir: 1 }); // dir: 1 asc, -1 desc
  ctrl.expandedWorkers = prop(Object.create(null)); // workerId -> boolean
  ctrl.workerGatewayRecords = Object.create(null); // workerId -> stream([])

  ctrl.applyWorkerUpdate = function (payload) {
    if (!payload || typeof payload.workerId === 'undefined' || payload.workerId === null) return;

    var current = ctrl.state() || { workers: [] };
    var workers = (current.workers || []).slice();
    var idx = workers.findIndex(function (w) {
      return w && Number(w.workerId) === Number(payload.workerId);
    });

    if (idx >= 0) workers[idx] = payload;
    else workers.push(payload);

    workers.sort(function (a, b) {
      return Number(a.workerId) - Number(b.workerId);
    });

    ctrl.state({ updatedAt: Date.now(), workers: workers });
  };

  ctrl.socketHandler = function (payload) {
    ctrl.applyWorkerUpdate(payload);
    m.redraw();
  };

  io.on('agent-metrics', ctrl.socketHandler);

  ctrl.init = function () {
    return loaders.requestWithLoader({
      url: '/api/v1/agent/metrics',
      method: 'GET',
    })
    .then(function (res) {
      var workers = res && Array.isArray(res.workers) ? res.workers : [];
      workers.sort(function (a, b) {
        return Number(a.workerId) - Number(b.workerId);
      });
      ctrl.state({ updatedAt: res && res.updatedAt ? res.updatedAt : Date.now(), workers: workers });
    })
    .catch(function (err) {
      ctrl.error(err || true);
    });
  };

  ctrl.loadHistory = function () {
    ctrl.historyLoading(true);
    return m.request({
      url: '/api/v1/agent/metrics/history',
      method: 'GET',
      params: {
        minutes: 60,
        window: '1m',
      },
    })
    .then(function (res) {
      ctrl.history(res || null);
      ctrl.historyError(null);
      ctrl.historyLoading(false);
    })
    .catch(function (err) {
      ctrl.historyError(err || true);
      ctrl.historyLoading(false);
    });
  };

  ctrl.init();
  ctrl.loadHistory();
  ctrl.historyTimer = setInterval(function () {
    ctrl.loadHistory().then(m.redraw);
  }, 60000);
};

page.onremove = function () {
  if (this.socketHandler) {
    io.off('agent-metrics', this.socketHandler);
  }
  if (this.historyTimer) {
    clearInterval(this.historyTimer);
  }
};

page.view = function () {
  var ctrl = this;
  var state = ctrl.state() || {};
  var workers = state.workers || [];

  if (ctrl.error()) {
    return m('div.mh3', [
      m(heading, { label: 'Agent Metrics', class: 'f-subheadline mb3 pb1 display' }),
      m('div.f4.red', 'Failed to load agent metrics.'),
    ]);
  }

  return m('div.mh3', [
    m('style', [
      '.agent-metrics-th { cursor: pointer; user-select: none; }',
      '.agent-metrics-th:hover { text-decoration: underline; }',
      '.agent-metrics-row { will-change: transform; transition: transform 220ms ease; }',
      '.agent-metrics-worker-header { cursor: pointer; }',
      '.agent-metrics-worker-headline { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }',
      '.agent-metrics-worker-metrics { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 10px; }',
      '.agent-metrics-tile { border: 1px solid #d8d8d8; padding: 6px 8px; background: transparent; }',
      '.agent-metrics-tile strong { font-weight: 600; }',
      '.agent-history-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 12px; }',
      '.agent-history-panel { border: 1px solid #d8d8d8; padding: 10px; min-height: 276px; }',
      '.agent-history-panel-title { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }',
      '.agent-history-latest { min-width: 96px; }',
      '.agent-history-chart { height: 220px; }',
      '.agent-history-chart > div { height: 220px; }',
      '.agent-history-panel .c3-legend-item { font-size: 11px; }',
    ].join('\n')),
    m(heading, { label: 'Agent Metrics', class: 'f-subheadline mb3 pb1 display' }, [
      state.updatedAt ? m('div.f4.mid-gray', 'Updated ' + formatAge(state.updatedAt) + ' ago') : null,
    ]),
    renderHistory(ctrl),
    m('div', renderWorkers(ctrl, workers)),
  ]);
};

function renderHistory(ctrl) {
  var history = ctrl.history();
  var latest = history && history.latest ? history.latest : {};

  if (ctrl.historyError()) {
    return m('div.mb3.f4.red', 'Failed to load polling history.');
  }

  if (!history) {
    return m('div.mb3.f4.mid-gray', ctrl.historyLoading() ? 'Loading polling history...' : 'No polling history yet.');
  }

  if (history.historyUnavailable) {
    return m('div.mb3.f4.dark-red', 'Polling history is unavailable. Live worker metrics are still shown below.');
  }

  return m('div.mb4', [
    m('div.agent-metrics-worker-metrics.mb3', [
      metricTile('History', (history.minutes || 60) + 'm / ' + (history.window || '1m')),
      metricTile('Poll', formatRate(latest.pollRps)),
      metricTile('Stale', formatWholeNumber(latest.staleGatewayCount)),
      metricTile('Next 30s', formatWholeNumber(latest.next30s)),
    ]),
    m('div.agent-history-grid', [
      m(agentHistoryChart, {
        chartId: 'agent-history-throughput',
        title: 'Throughput',
        subtitle: 'Polls per second',
        history: history,
        valueFormatter: formatRate,
        series: [
          { key: 'pollRps', label: 'Poll/s', color: '#1f77b4' },
        ],
      }),
      m(agentHistoryChart, {
        chartId: 'agent-history-queue',
        title: 'Queue Horizon',
        subtitle: 'Resources due soon',
        history: history,
        valueFormatter: formatWholeNumber,
        series: [
          { key: 'dueNow', label: 'Due now', color: '#d62728' },
          { key: 'next5s', label: 'Next 5s', color: '#9467bd' },
          { key: 'next30s', label: 'Next 30s', color: '#ff7f0e' },
        ],
      }),
      m(agentHistoryChart, {
        chartId: 'agent-history-freshness',
        title: 'Freshness',
        subtitle: 'Polling activity age',
        history: history,
        valueFormatter: formatSecondsFromSeconds,
        series: [
          { key: 'latestPollAgeMs', label: 'Poll age', color: '#1f77b4', transform: msToSeconds },
        ],
      }),
    ]),
  ]);
}

function renderWorkers(ctrl, workers) {
  var expanded = ctrl.expandedWorkers() || Object.create(null);
  var sort = ctrl.sort() || { key: 'workerId', dir: 1 };
  var out = [];

  (workers || []).slice().sort(function (a, b) {
    return Number(a.workerId) - Number(b.workerId);
  }).forEach(function (worker) {
    var workerId = worker && typeof worker.workerId !== 'undefined' ? worker.workerId : '-';
    var isOpen = Boolean(expanded[String(workerId)]);
    var summary = worker && worker.summary ? worker.summary : null;
    var scheduler = summary && summary.scheduler ? summary.scheduler : {};
    var schedulerSleep = scheduler.sleep || {};
    var schedulerDueLag = scheduler.dueLag || {};
    var schedulerWindow = scheduler.window || {};
    var pollWindow = summary && summary.poll && summary.poll.window ? summary.poll.window : {};
    var writeWindow = summary && summary.write && summary.write.window ? summary.write.window : {};
    var hostname = summary && summary.node && summary.node.hostname ? summary.node.hostname : null;

    var containerClass = 'div.cf.bw1.mv2.b--grey' + (isOpen ? '.pv3.bt.pl2.bl.pr3' : '.pa4.ba');
    var toggle = function () {
      var next = Object.assign(Object.create(null), expanded);
      next[String(workerId)] = !isOpen;
      ctrl.expandedWorkers(next);
    };

    out.push(
      m(containerClass, {
        style: {
          'border-left-style': isOpen ? 'dotted' : 'solid'
        }
      }, [
        m('div.agent-metrics-worker-header', { onclick: toggle }, [
          m('div.flex.justify-between', [
            m('div', [
              m('div.f3', 'Worker ' + workerId),
              hostname ? m('div.f5.moon-gray.mt1', hostname) : null,
              summary ? m('div.f5.moon-gray.agent-metrics-worker-metrics', [
                metricTile('Uptime', formatDuration(summary.uptimeMs)),
                metricTile('Poll', formatRate(summary.poll && summary.poll.rps)),
                metricTile('Poll err% (5m)', formatRatioPercent(pollWindow.errPct)),
                metricTile('Write', formatRate(summary.write && summary.write.rps)),
                metricTile('Write err% (5m)', formatRatioPercent(writeWindow.errPct)),
                metricTile('Queued res', summary.queue && Number.isFinite(Number(summary.queue.resources)) ? summary.queue.resources : '-'),
                metricTile('Queued writes', summary.queue && Number.isFinite(Number(summary.queue.writes)) ? summary.queue.writes : '-'),
                metricTile('Sleep EWMA', formatDuration(schedulerSleep.ewmaMs)),
                metricTile('Due lag EWMA', formatMs(schedulerDueLag.ewmaMs)),
                metricTile('Early wakes (5m)', formatPerMinute(schedulerWindow.earlyWakePerMinute)),
                summary.lastErrorAt ? metricTile('Last error', formatAge(summary.lastErrorAt) + ' ago') : null,
              ]) : m('div.f5.moon-gray.agent-metrics-worker-metrics', metricTile('Status', 'No summary')),
              summary ? renderAlarmEnabledCacheMetrics(summary.alarmEnabledCache) : null,
            ]),
            m('div.ml3.flex.flex-column.justify-end', { style: { minHeight: '44px' } }, [
              m('i.blue.mb1.' + (isOpen ? 'ion-close f2' : 'ion-chevron-down f2')),
            ]),
          ]),
          summary && summary.warnings && summary.warnings.length ? m('div.mt2.f5.orange', [
            m('strong', 'Warnings: '),
            m('span', summary.warnings.join(' | ')),
          ]) : null,
          summary && summary.lastError ? m('div.mt2.f5.red', [
            m('strong', 'Last error: '),
            m('span', String(summary.lastError)),
          ]) : null,
        ]),

        isOpen ? m('div.mt3', renderWorkerGateways(ctrl, worker, sort)) : null,
      ])
    );
  });

  if (!out.length) {
    return [m('div.f4.mid-gray', 'No workers reporting metrics yet.')];
  }

  return out;
}

function renderAlarmEnabledCacheMetrics(cacheSummary) {
  if (!cacheSummary || !cacheSummary.enabled) return null;
  var metrics = cacheSummary.metrics || {};
  var windowMetrics = metrics.window || {};
  var evaluation = windowMetrics.evaluation || {};
  var cache = windowMetrics.cache || {};
  var sync = metrics.sync || {};
  var state = cacheSummary.state || {};
  var syncMeta = state.syncMeta || {};
  var status = cacheSummary.stale ? 'stale' : 'fresh';
  var syncDurationMs = Number.isFinite(Number(syncMeta.durationMs)) ? syncMeta.durationMs : sync.lastDurationMs;

  return m('div.f5.moon-gray.agent-metrics-worker-metrics', [
    metricTile('Alarm cache', status),
    metricTile('Sync owner', cacheSummary.syncOwner || '-'),
    metricTile('Sync status', syncMeta.status || '-'),
    metricTile('Enabled alarms', Number.isFinite(Number(state.enabledCount)) ? state.enabledCount : '-'),
    metricTile('Skipped alarms (5m)', formatWindowCounter(
      evaluation.skippedDisabledClear,
      evaluation.skippedDisabledClearPct,
      evaluation.skippedDisabledClearRps
    )),
    metricTile('Full alarms (5m)', formatWindowCounter(
      evaluation.full,
      evaluation.fullPct,
      evaluation.fullRps
    )),
    metricTile('Fail-open (15m)', formatWindowEventCount(cache.failOpen, cache.lastFailOpenAt)),
    metricTile('Cache stale (15m)', formatWindowEventCount(cache.stale, cache.lastStaleAt)),
    metricTile('Sync age', state.syncedAt ? formatAge(state.syncedAt) : '-'),
    metricTile('Sync chunks', Number.isFinite(Number(syncMeta.chunks)) ? syncMeta.chunks : '-'),
    metricTile('Sync duration', Number.isFinite(Number(syncDurationMs)) ? formatDuration(syncDurationMs) : '-'),
  ]);
}

function renderWorkerGateways(ctrl, worker, sort) {
  var workerOnlyRows = sortRows(flattenRows([worker]), sort);
  var workerId = worker && typeof worker.workerId !== 'undefined' ? worker.workerId : '-';
  var rowIds = workerOnlyRows
    .map(function (row) { return row && row.rowKey ? row.rowKey : null; })
    .filter(function (rowKey) { return !!rowKey; });

  if (!ctrl.workerGatewayRecords[String(workerId)]) {
    ctrl.workerGatewayRecords[String(workerId)] = prop([]);
  }
  var recordsToShow = ctrl.workerGatewayRecords[String(workerId)];
  var refreshKey = worker && worker.updatedAt ? worker.updatedAt : null;

  return m('div.overflow-hidden', [
    renderSinceWorkerStart(worker && worker.summary),
    rowIds.length ? m(alarmsTablePagination, {
      data: rowIds,
      url: '/api/v1/agent/metrics/gateways',
      recordsToShow: recordsToShow,
      location: { '@rid': 'agent-metrics-worker-' + workerId },
      numberOfColumns: 26,
      refreshKey: refreshKey,
    }) : null,
    m('div.dragscroll-container', [
      m('div.dragscroll', { oncreate: dragscroll }, [
        m('table.w-100.f4.mb2.pt2.mr4', [
          m('thead', [
            m('tr', [
              sortableTh(ctrl, 'Host', 'gateway'),
              sortableTh(ctrl, 'Status', 'status'),
              sortableTh(ctrl, 'Last Poll', 'lastPollAt'),
              sortableTh(ctrl, 'Loop EWMA', 'avgLoopMs'),
              sortableTh(ctrl, 'Poll EWMA', 'avgPollMs'),
              sortableTh(ctrl, 'Poll Err% Since Start', 'pollErrPct'),
              sortableTh(ctrl, 'Write EWMA', 'avgWriteMs'),
              sortableTh(ctrl, 'Write Err% Since Start', 'writeErrPct'),
              sortableTh(ctrl, 'Resources', 'resources'),
              sortableTh(ctrl, 'Writes', 'writes'),
            ])
          ]),
          m('tbody', renderRows(ctrl, recordsToShow(), { showWorker: false })),
        ]),
      ]),
    ]),
  ]);
}

function renderSinceWorkerStart(summary) {
  if (!summary) return null;
  var poll = summary.poll || {};
  var write = summary.write || {};
  var scheduler = summary.scheduler || {};
  var alarmMetrics = summary.alarmEnabledCache && summary.alarmEnabledCache.metrics
    ? summary.alarmEnabledCache.metrics
    : {};
  var alarmEvaluation = alarmMetrics.evaluation || {};
  var alarmCache = alarmMetrics.cache || {};

  return m('div.mb3', [
    m('div.f5.moon-gray', 'Since worker start'),
    m('div.f5.moon-gray.agent-metrics-worker-metrics', [
      metricTile('Polls', formatWholeNumber(poll.count)),
      metricTile('Poll errors', formatWholeNumber(poll.errCount)),
      metricTile('Writes', formatWholeNumber(write.count)),
      metricTile('Write errors', formatWholeNumber(write.errCount)),
      metricTile('Early wakes', formatWholeNumber(scheduler.earlyWakeCount)),
      metricTile('Full alarms', formatWholeNumber(alarmEvaluation.full)),
      metricTile('Skipped alarms', formatWholeNumber(alarmEvaluation.skippedDisabledClear)),
      metricTile('Fail-open', formatWholeNumber(alarmCache.failOpen)),
      metricTile('Cache stale', formatWholeNumber(alarmCache.stale)),
    ]),
  ]);
}

function sortableTh(ctrl, label, key) {
  var sort = ctrl.sort() || { key: 'workerId', dir: 1 };
  var active = sort.key === key;
  var arrow = active ? (sort.dir === 1 ? ' ↑' : ' ↓') : '';

  return m('th.ph0.tl.agent-metrics-th', {
    onclick: function () {
      var nextDir = 1;
      if (active) nextDir = sort.dir === 1 ? -1 : 1;
      ctrl.sort({ key: key, dir: nextDir });
    },
    title: 'Sort by ' + label,
  }, label + arrow);
}

function flattenRows(workers) {
  var rows = [];
  (workers || []).forEach(function (worker) {
    var workerId = worker && typeof worker.workerId !== 'undefined' ? worker.workerId : '-';
    var gateways = worker && Array.isArray(worker.gateways) ? worker.gateways : [];

    if (!gateways.length) {
      rows.push({
        rowKey: String(workerId) + ':-',
        workerId: workerId,
        queueId: null,
        gatewayLabel: '-',
        status: 'unknown',
        lastPollAt: null,
        avgLoopMs: null,
        avgPollMs: null,
        pollErrPct: null,
        avgWriteMs: null,
        writeErrPct: null,
        resources: null,
        writes: null,
        gateway: null,
      });
      return;
    }

    gateways.forEach(function (gateway) {
      var queueId = gateway && gateway.queueId ? String(gateway.queueId) : null;
      var gatewayInfo = gateway && gateway.gateway ? gateway.gateway : {};
      var gatewayLabel = gatewayInfo.name || gatewayInfo['@rid'] || queueId || '-';
      var pollCount = gateway && gateway.poll ? Number(gateway.poll.count) : 0;
      var pollErrCount = gateway && gateway.poll ? Number(gateway.poll.errCount) : 0;
      var writeCount = gateway && gateway.write ? Number(gateway.write.count) : 0;
      var writeErrCount = gateway && gateway.write ? Number(gateway.write.errCount) : 0;

      rows.push({
        rowKey: String(workerId) + ':' + (queueId || gatewayLabel || '-'),
        workerId: workerId,
        queueId: queueId,
        gatewayLabel: gatewayLabel,
        status: gateway && gateway.status ? gateway.status : 'unknown',
        lastPollAt: gateway && gateway.lastPollAt ? Number(gateway.lastPollAt) : null,
        avgLoopMs: gateway && gateway.pollLoop ? Number(gateway.pollLoop.ewmaMs) : null,
        avgPollMs: gateway && gateway.poll ? Number(gateway.poll.ewmaMs) : null,
        pollErrPct: pollCount ? (pollErrCount / pollCount) : null,
        avgWriteMs: gateway && gateway.write ? Number(gateway.write.ewmaMs) : null,
        writeErrPct: writeCount ? (writeErrCount / writeCount) : null,
        resources: gateway && gateway.queue && gateway.queue.resources != null ? Number(gateway.queue.resources) : null,
        writes: gateway && gateway.queue && gateway.queue.writes != null ? Number(gateway.queue.writes) : null,
        gateway: gateway,
      });
    });
  });

  return rows;
}

function sortRows(rows, sort) {
  var key = sort && sort.key ? sort.key : 'workerId';
  var dir = sort && sort.dir === -1 ? -1 : 1;

  function cmp(a, b) {
    var av = a ? a[key] : null;
    var bv = b ? b[key] : null;

    // Status: map to severity order.
    if (key === 'status') {
      av = agentGatewayStatus.statusRank(av);
      bv = agentGatewayStatus.statusRank(bv);
    }

    // Gateway label is stored as gatewayLabel; accept `gateway` sort key.
    if (key === 'gateway') {
      av = a ? a.gatewayLabel : null;
      bv = b ? b.gatewayLabel : null;
    }

    // Nulls last.
    if (av === null || typeof av === 'undefined' || (typeof av === 'number' && !Number.isFinite(av))) {
      if (bv === null || typeof bv === 'undefined' || (typeof bv === 'number' && !Number.isFinite(bv))) return 0;
      return 1;
    }
    if (bv === null || typeof bv === 'undefined' || (typeof bv === 'number' && !Number.isFinite(bv))) return -1;

    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return String(av).localeCompare(String(bv));
  }

  return (rows || []).slice().sort(function (a, b) {
    var primary = cmp(a, b);
    if (primary !== 0) return primary * dir;
    // Deterministic tie-breaker.
    return String(a.rowKey).localeCompare(String(b.rowKey));
  });
}

function renderRows(ctrl, rows, options) {
  options = options || {};
  var showWorker = options.showWorker !== false;
  var out = [];

  (rows || []).forEach(function (row, index) {
    var gateway = row && row.gateway ? row.gateway : null;
    var pollLoop = row && row.pollLoop ? row.pollLoop : null;
    var poll = row && row.poll ? row.poll : null;
    var write = row && row.write ? row.write : null;
    var queue = row && row.queue ? row.queue : null;
    var status = row && row.status ? row.status : 'unknown';
    var lastPollAt = row && row.lastPollAt ? Number(row.lastPollAt) : null;
    var rowKey = row && row.rowKey ? String(row.rowKey) : ('placeholder-' + index);
    var gatewayInfo = gateway || {};
    var routeInfo = getGatewayRouteInfo(row, gatewayInfo);
    var fallbackLabel = row && row.gatewayLabel
      ? row.gatewayLabel
      : (gatewayInfo.name || gatewayInfo['@rid'] || row.queueId || '-');
    var gatewayLabel = fallbackLabel;
    var displayLabel = gatewayInfo.name || gatewayLabel;

    var cells = [];
    if (showWorker) {
      cells.push(m('td.tl.nowrap', row.workerId));
    }
    cells.push(m('td.tl.nowrap', routeInfo
      ? m(
        m.route.Link,
        { href: routeInfo.href, class: 'blue', title: displayLabel || routeInfo.label },
        shortLabel(displayLabel)
      )
      : shortLabel(displayLabel)
    ));
    cells.push(m('td.tl.nowrap', m('span.' + agentGatewayStatus.statusClass(status), status)));
    cells.push(m('td.tl.nowrap', lastPollAt ? formatAge(lastPollAt) + ' ago' : '-'));
    cells.push(m('td.tl.nowrap', formatMs(pollLoop ? pollLoop.ewmaMs : row.avgLoopMs)));
    cells.push(m('td.tl.nowrap', formatMs(poll ? poll.ewmaMs : row.avgPollMs)));
    cells.push(m('td.tl.nowrap', formatPercent(poll ? poll.errCount : null, poll ? poll.count : null)));
    cells.push(m('td.tl.nowrap', formatMs(write ? write.ewmaMs : row.avgWriteMs)));
    cells.push(m('td.tl.nowrap', formatPercent(write ? write.errCount : null, write ? write.count : null)));
    cells.push(m('td.tl.nowrap', queue && queue.resources != null ? queue.resources : (row.resources != null ? row.resources : '-')));
    cells.push(m('td.tl.nowrap', queue && queue.writes != null ? queue.writes : (row.writes != null ? row.writes : '-')));

    out.push(
      m(
        'tr.agent-metrics-row',
        {
          key: rowKey,
          oncreate: animateRow,
          onupdate: animateRow,
        },
        cells
      )
    );
  });

  return out;
}

function animateRow(vnode) {
  // FLIP (First-Last-Invert-Play) for row reordering.
  // We only animate when we have a previous rect (i.e. after first render).
  if (!vnode || !vnode.dom) return;
  var el = vnode.dom;
  var key = vnode && vnode.attrs && vnode.attrs.key ? String(vnode.attrs.key) : null;
  if (!key) return;

  var prev = lastRowRects.get(key);
  var next = el.getBoundingClientRect();
  lastRowRects.set(key, next);
  if (!prev) return;

  var dx = prev.left - next.left;
  var dy = prev.top - next.top;
  if (!dx && !dy) return;

  // Jump back to the previous position...
  el.style.transition = 'transform 0s';
  el.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';

  // ...then animate to the new position.
  requestAnimationFrame(function () {
    el.style.transition = '';
    el.style.transform = '';
  });
}

function formatMs(value) {
  var n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return Math.round(n) + 'ms';
}

function formatWholeNumber(value) {
  var n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return String(Math.round(n));
}

function msToSeconds(value) {
  var n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n / 1000;
}

function formatSecondsFromSeconds(value) {
  var n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (n < 10) return n.toFixed(1) + 's';
  return Math.round(n) + 's';
}

function formatPercent(errorCount, count) {
  var errors = Number(errorCount) || 0;
  var total = Number(count) || 0;
  if (!total) return '-';
  return Math.round((errors / total) * 100) + '%';
}

function formatRatioPercent(ratio) {
  if (ratio === null || typeof ratio === 'undefined' || ratio === '') return '-';
  var r = Number(ratio);
  if (!Number.isFinite(r)) return '-';
  return Math.round(r * 100) + '%';
}

function formatRate(value) {
  if (value === null || typeof value === 'undefined' || value === '') return '-';
  var n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toFixed(2) + '/s';
}

function formatPerMinute(value) {
  if (value === null || typeof value === 'undefined' || value === '') return '-';
  var n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toFixed(1) + '/min';
}

function formatWindowCounter(count, ratio, rate) {
  var n = Number(count);
  if (!Number.isFinite(n)) return '-';
  var parts = [formatWholeNumber(n)];
  var formattedRatio = formatRatioPercent(ratio);
  var formattedRate = formatRate(rate);
  if (formattedRatio !== '-') parts.push(formattedRatio);
  if (formattedRate !== '-') parts.push(formattedRate);
  return parts.join(' / ');
}

function formatWindowEventCount(count, lastAt) {
  var n = Number(count);
  if (!Number.isFinite(n)) return '-';
  var value = formatWholeNumber(n);
  if (Number.isFinite(Number(lastAt)) && Number(lastAt) > 0) {
    value += ' / last ' + formatAge(lastAt) + ' ago';
  }
  return value;
}

function formatAge(timestampMs) {
  var ts = Number(timestampMs);
  if (!Number.isFinite(ts) || ts <= 0) return '-';
  var seconds = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (seconds < 60) return seconds + 's';
  var minutes = Math.round(seconds / 60);
  if (minutes < 60) return minutes + 'm';
  var hours = Math.round(minutes / 60);
  return hours + 'h';
}

function shortLabel(value) {
  if (!value) return '-';
  var s = String(value);
  if (s.length <= 28) return s;
  return s.slice(0, 14) + '...' + s.slice(-8);
}

function getGatewayRouteInfo(row, gatewayInfo) {
  var cls = gatewayInfo && gatewayInfo['@class'] ? gatewayInfo['@class'] : null;
  var rid = gatewayInfo && gatewayInfo['@rid'] ? gatewayInfo['@rid'] : null;
  if (!cls || !rid) return null;
  var clsPath = String(cls).toLowerCase();
  var ridStr = String(rid);
  var ridClean = ridStr.charAt(0) === '#' ? ridStr.slice(1) : ridStr;
  return {
    href: '/' + clsPath + '/' + ridClean,
    label: clsPath + '/' + ridClean,
  };
}

function formatDuration(ms) {
  var n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '-';
  var seconds = Math.floor(n / 1000);
  if (seconds < 60) return seconds + 's';
  var minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + 'm';
  var hours = Math.floor(minutes / 60);
  if (hours < 48) return hours + 'h';
  var days = Math.floor(hours / 24);
  return days + 'd';
}

function metricKv(label, value) {
  if (value === null || typeof value === 'undefined') return null;
  return m('span', [m('strong', label + ':'), ' ', String(value)]);
}

function metricTile(label, value) {
  if (value === null || typeof value === 'undefined') return null;
  return m('span.agent-metrics-tile', [m('strong', label + ':'), ' ', String(value)]);
}

module.exports = page;
